export function getBase64URL({ file }: { file: File }): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => resolve(e.target?.result as string) // DataURL string
    reader.onerror = () => reject(new Error('Failed to read file')) // avoid silent hangs

    reader.readAsDataURL(file)
  })
}

export async function getResizedDataURL({
  base64URL,
  contentType,
  maxDim,
  quality = 0.9,
}: {
  base64URL: string
  contentType: 'image/jpeg' | 'image/png'
  maxDim?: number
  quality?: number
}): Promise<{ base64URL: string; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.decoding = 'async' // hint: decode off main thread when possible
    img.crossOrigin = 'anonymous' // safe; helps if you later feed it non-data URLs

    img.onload = () => {
      const originalWidth = img.naturalWidth || img.width // natural* is the real pixel size
      const originalHeight = img.naturalHeight || img.height

      // Default: preserve dimensions
      let targetWidth = originalWidth
      let targetHeight = originalHeight

      // If maxDim is set: scale down to fit inside maxDim (never upscale)
      if (maxDim && maxDim > 0) {
        const scale = Math.min(
          1,
          maxDim / Math.max(originalWidth, originalHeight)
        )
        targetWidth = Math.max(1, Math.round(originalWidth * scale))
        targetHeight = Math.max(1, Math.round(originalHeight * scale))
      }

      const canvas = document.createElement('canvas')
      canvas.width = targetWidth
      canvas.height = targetHeight

      const ctx = canvas.getContext('2d')
      if (!ctx) return reject(new Error('Canvas not supported'))

      // Important: don't paint a background -> PNG transparency stays intact
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight)

      // JPEG quality must be in [0..1]
      const q = Math.min(1, Math.max(0, quality))

      const resizedDataURL = canvas.toDataURL(
        contentType,
        contentType === 'image/jpeg' ? q : undefined // quality ignored for PNG
      )

      resolve({
        base64URL: resizedDataURL,
        width: targetWidth,
        height: targetHeight,
      })
    }

    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = base64URL
  })
}

export async function trimImageDataURL({
  base64URL,
  contentType,
  padding = 4,
}: {
  base64URL: string
  contentType: 'image/jpeg' | 'image/png'
  padding?: number
}): Promise<{ base64URL: string; width: number; height: number }> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image()
    el.onload = () => resolve(el)
    el.onerror = () => reject(new Error('Failed to load image for trimming'))
    el.src = base64URL
  })

  const w = img.naturalWidth || img.width
  const h = img.naturalHeight || img.height

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h

  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('Canvas not supported')

  ctx.drawImage(img, 0, 0)
  const { data } = ctx.getImageData(0, 0, w, h)

  const isBackground = (i: number): boolean => {
    if (data[i + 3]! < 10) return true
    if (contentType !== 'image/png' || data[i + 3]! === 255) {
      return data[i]! > 240 && data[i + 1]! > 240 && data[i + 2]! > 240
    }
    return false
  }

  let minX = w
  let maxX = 0
  let minY = h
  let maxY = 0

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!isBackground((y * w + x) * 4)) {
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
    }
  }

  if (minX > maxX || minY > maxY) {
    return { base64URL, width: w, height: h }
  }

  const x0 = Math.max(0, minX - padding)
  const y0 = Math.max(0, minY - padding)
  const x1 = Math.min(w, maxX + 1 + padding)
  const y1 = Math.min(h, maxY + 1 + padding)
  const cropW = x1 - x0
  const cropH = y1 - y0

  const out = document.createElement('canvas')
  out.width = cropW
  out.height = cropH
  const outCtx = out.getContext('2d')
  if (!outCtx) throw new Error('Canvas not supported')

  outCtx.drawImage(canvas, x0, y0, cropW, cropH, 0, 0, cropW, cropH)

  const trimmed = out.toDataURL(
    contentType,
    contentType === 'image/jpeg' ? 0.9 : undefined
  )

  return { base64URL: trimmed, width: cropW, height: cropH }
}

export function shouldCheckAlphaForType(mime: string): boolean {
  return (
    mime === 'image/webp' ||
    mime === 'image/avif' ||
    mime === 'image/heic' ||
    mime === 'image/heif'
  )
}

export async function detectAlphaFromDataUrl(
  dataUrl: string,
  sampleStep = 8
): Promise<boolean> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image()
    el.onload = () => resolve(el)
    el.onerror = () =>
      reject(new Error('Failed to decode image for alpha detection'))
    el.src = dataUrl
  })

  const w = img.naturalWidth || img.width
  const h = img.naturalHeight || img.height

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h

  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('2D canvas not available')

  ctx.clearRect(0, 0, w, h)
  ctx.drawImage(img, 0, 0)

  // Sample pixels for speed (good enough for logos/icons)
  const imageData = ctx.getImageData(0, 0, w, h)
  const { data, width, height } = imageData

  for (let y = 0; y < height; y += sampleStep) {
    for (let x = 0; x < width; x += sampleStep) {
      const aIndex = (y * width + x) * 4 + 3
      if (data[aIndex] !== 255) return true
    }
  }
  return false
}
